import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePeriods } from "@/hooks/usePeriods";
import { usePermissions } from "@/hooks/usePermissions";
import { handleRpcError } from "@/lib/rpcErrors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Lock, Unlock, ClipboardCheck, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

interface ChecklistItem {
  label: string;
  count: number;
  pass: boolean;
}

export const PeriodTable = () => {
  const isMobile = useIsMobile();
  const { data: periods, isLoading } = usePeriods();
  const { can, isAdmin } = usePermissions();
  const canClosePeriods = isAdmin || can("close_periods");
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [closeModal, setCloseModal] = useState<{ id: string; name: string } | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[] | null>(null);
  const [allClear, setAllClear] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenModal, setReopenModal] = useState<{ id: string; name: string } | null>(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);

  const openCloseModal = async (id: string, name: string) => {
    setCloseModal({ id, name });
    setChecklist(null);
    setAllClear(false);
    setCheckLoading(true);
    try {
      const { data, error } = await supabase.rpc("validate_period_close", { p_period_id: id });
      if (error) throw error;
      const res = data as any;
      setChecklist(res.items ?? []);
      setAllClear(!!res.all_clear);
    } catch (err) {
      handleRpcError(err);
      setCloseModal(null);
    } finally {
      setCheckLoading(false);
    }
  };

  const confirmClose = async () => {
    if (!closeModal) return;
    setClosing(true);
    try {
      const { data, error } = await supabase.rpc("close_accounting_period", { p_period_id: closeModal.id });
      if (error) throw error;
      const res = data as any;
      if (res?.success) {
        toast.success("Period closed successfully");
        qc.invalidateQueries({ queryKey: ["accounting_periods"] });
        setCloseModal(null);
      } else {
        toast.error(res?.error || "Cannot close period");
      }
    } catch (err) {
      handleRpcError(err);
    } finally {
      setClosing(false);
    }
  };

  const confirmReopen = async () => {
    if (!reopenModal) return;
    setReopening(true);
    try {
      const { data, error } = await supabase.rpc("reopen_accounting_period", {
        p_period_id: reopenModal.id,
        p_reason: reopenReason || null,
      });
      if (error) throw error;
      const res = data as any;
      if (res?.success) {
        toast.success("Period reopened");
        qc.invalidateQueries({ queryKey: ["accounting_periods"] });
        setReopenModal(null);
        setReopenReason("");
      } else {
        toast.error(res?.error || "Cannot reopen period");
      }
    } catch (err) {
      handleRpcError(err);
    } finally {
      setReopening(false);
    }
  };

  if (isLoading) return <p className="p-4 text-muted-foreground">Loading…</p>;
  if (!periods?.length) return <p className="p-4 text-muted-foreground">No accounting periods found.</p>;

  return (
    <>
      {isMobile ? (
        <div className="space-y-3">
          {periods.map((p) => (
            <Card key={p.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{p.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {format(new Date(p.start_date), "MMM d")} — {format(new Date(p.end_date), "MMM d, yyyy")}
                    </p>
                  </div>
                  <Badge variant={p.is_locked ? "destructive" : "secondary"}>
                    {p.is_locked ? "Closed" : "Open"}
                  </Badge>
                </div>
                {canClosePeriods && (
                  <div className="mt-3">
                    {!p.is_locked ? (
                      <Button size="sm" variant="default" className="w-full" disabled={busy === p.id} onClick={() => openCloseModal(p.id, p.name)}>
                        <ClipboardCheck className="mr-1 h-3 w-3" /> Close Period
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full" disabled={busy === p.id} onClick={() => { setReopenModal({ id: p.id, name: p.name }); setReopenReason(""); }}>
                        <Unlock className="mr-1 h-3 w-3" /> Reopen
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>End</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {periods.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{format(new Date(p.start_date), "MMM d, yyyy")}</TableCell>
                <TableCell>{format(new Date(p.end_date), "MMM d, yyyy")}</TableCell>
                <TableCell>
                  <Badge variant={p.is_locked ? "destructive" : "secondary"}>
                    {p.is_locked ? "Closed" : "Open"}
                  </Badge>
                </TableCell>
                <TableCell className="flex gap-2">
                  {canClosePeriods && !p.is_locked ? (
                    <Button size="sm" variant="default" disabled={busy === p.id} onClick={() => openCloseModal(p.id, p.name)}>
                      <ClipboardCheck className="mr-1 h-3 w-3" /> Close Period
                    </Button>
                  ) : canClosePeriods && p.is_locked ? (
                    <Button size="sm" variant="outline" disabled={busy === p.id} onClick={() => { setReopenModal({ id: p.id, name: p.name }); setReopenReason(""); }}>
                      <Unlock className="mr-1 h-3 w-3" /> Reopen
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Close Period Checklist Modal */}
      <Dialog open={!!closeModal} onOpenChange={(o) => !o && setCloseModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Close Period: {closeModal?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {checkLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Running pre-close checks…
              </div>
            )}
            {checklist?.map((item, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  {item.pass ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-destructive" />
                  )}
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
                <Badge variant={item.pass ? "secondary" : "destructive"}>
                  {item.count}
                </Badge>
              </div>
            ))}
            {checklist && !allClear && (
              <p className="text-sm text-destructive font-medium">
                Resolve all blocking items before closing this period.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseModal(null)}>Cancel</Button>
            <Button disabled={!allClear || closing} onClick={confirmClose}>
              {closing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
              Close Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Period Modal */}
      <Dialog open={!!reopenModal} onOpenChange={(o) => !o && setReopenModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reopen Period: {reopenModal?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Reopening a closed period allows new transactions to be posted. This action is audited.
            </p>
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Input value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Why is this period being reopened?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenModal(null)}>Cancel</Button>
            <Button variant="destructive" disabled={reopening} onClick={confirmReopen}>
              {reopening ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Unlock className="h-4 w-4 mr-1" />}
              Reopen Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};